import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Flex,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  HStack,
  Button,
  Progress,
  Box,
  VStack,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { FiGlobe, FiRefreshCw, FiClock, FiCheckCircle } from "react-icons/fi";
import JobsPageHeader from "../../../components/JobsPageHeader";
import { LoadingIndicator } from "../../../components/LoadingIndicator";

import WithAppLayout from "../../../components/WithAppLayout";
import { 
  GET_JOB_SCRAPING_SESSIONS, 
  GET_JOB_SCRAPING_PROGRESS,
  TRIGGER_JOB_SCRAPING
} from "../../../graphql/job_scraping";
import { UPDATE_AGENCY_JOB_WEBSITE_AND_TRIGGER_SCRAPING } from "../../../graphql/agencies";

function SyncJobsPage({ currentUser, loading }) {
  const toast = useToast();
  const [isTriggeringSync, setIsTriggeringSync] = useState(false);
  const [jobWebsiteUrl, setJobWebsiteUrl] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(true);

  // Mutation for triggering job scraping
  const [triggerJobScraping] = useMutation(TRIGGER_JOB_SCRAPING, {
    onCompleted: (data) => {
      if (data.triggerJobScraping) {
        toast({
          title: "Job scraping started",
          description: "Your website is now being scanned for job listings.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        // Refetch sessions to get the new session
        refetchSessions();
      }
    },
    onError: (error) => {
      toast({
        title: "Failed to start job scraping",
        description: error.message || "An error occurred while starting the scraping process.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  });

  // Mutation for updating agency job website and triggering scraping
  const [updateAgencyJobWebsite, { loading: isUpdating }] = useMutation(
    UPDATE_AGENCY_JOB_WEBSITE_AND_TRIGGER_SCRAPING,
    {
      onCompleted: (data) => {
        toast({
          title: "Job website updated successfully",
          description: "Scraping has been triggered and the page will refresh.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // Refetch sessions to get the new session and refresh the page
        refetchSessions();
        // Refresh the page to show the connected state
        window.location.reload();
      },
      onError: (error) => {
        toast({
          title: "Failed to update job website",
          description: error.message || "An error occurred while updating the job website.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  );

  // Query to get the latest job scraping session
  const { data: sessionsData, loading: sessionsLoading, refetch: refetchSessions } = useQuery(GET_JOB_SCRAPING_SESSIONS, {
    variables: { limit: 1, offset: 0 },
    skip: !currentUser?.agency?.jobWebsite,
    pollInterval: 5000, // Poll every 5 seconds when there's an active session
  });

  const latestSession = sessionsData?.jobScrapingSessions?.[0];

  // Function to validate URL
  const validateUrl = (url) => {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  // Function to handle job website submission
  const handleJobWebsiteSubmit = async () => {
    if (!jobWebsiteUrl.trim()) {
      setIsValidUrl(false);
      return;
    }

    if (!validateUrl(jobWebsiteUrl)) {
      setIsValidUrl(false);
      return;
    }

    setIsValidUrl(true);

    try {
      await updateAgencyJobWebsite({
        variables: {
          jobWebsite: jobWebsiteUrl.trim(),
        },
      });
    } catch (error) {
      console.error('Error updating job website:', error);
    }
  };

  // Query to get progress for in-progress sessions
  const { data: progressData } = useQuery(GET_JOB_SCRAPING_PROGRESS, {
    variables: { sessionId: latestSession?.id },
    skip: !latestSession?.id || latestSession?.status === 'COMPLETED',
    pollInterval: 2000, // Poll every 2 seconds for real-time progress
  });

  // Function to check if there's a running session and trigger scraping
  const handleTriggerScraping = async () => {
    // Check if there's already a running session
    const runningSessions = ['PENDING', 'IN_PROGRESS'];
    if (latestSession && runningSessions.includes(latestSession.status)) {
      toast({
        title: "Scraping already in progress",
        description: "There's already a job scraping session running. Please wait for it to complete.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsTriggeringSync(true);
    
    try {
      await triggerJobScraping();
    } catch (error) {
      console.error('Error triggering job scraping:', error);
    } finally {
      setIsTriggeringSync(false);
    }
  };

  if (loading || (currentUser?.agency?.jobWebsite && sessionsLoading)) {
    return <LoadingIndicator />;
  }

  // Function to render progress content for active sessions
  const renderProgressContent = () => {
    if (!latestSession || latestSession.status === 'COMPLETED') return null;

    const progressInfo = progressData?.jobScrapingProgress || [];

    return (
      <Center 
        flexDir="column" 
        width="100%" 
        minH="500px" 
        border="1px" 
        borderColor="neutral.600" 
        borderRadius="12px" 
        gap="20px"
        p="40px"
      >
        <VStack spacing={4} align="center">
          <Spinner size="xl" color="blue.500" />
          <Text fontSize="2xl" fontWeight="bold">
            Scraping Jobs in Progress
          </Text>
          <Text color="gray.600" textAlign="center" maxW="400px">
            We're currently scanning {currentUser?.agency?.jobWebsite} for job listings
          </Text>
          
          {/*
          <Box width="100%" maxW="400px">
            <Text fontSize="sm" color="gray.600" mb={2}>
              Overall Progress: {overallProgress}%
            </Text>
            <Progress 
              value={overallProgress} 
              colorScheme="blue" 
              size="lg" 
              borderRadius="md"
            />
          </Box>
          */}

          <VStack spacing={2} align="start" width="100%" maxW="400px">
            <Text fontSize="md" fontWeight="semibold">Session Details:</Text>
            <HStack>
              <FiClock />
              <Text fontSize="sm">Status: {latestSession.status}</Text>
            </HStack>
            {/*<Text fontSize="sm">Websites: {latestSession.totalWebsites}</Text>*/}
            <Text fontSize="sm">Jobs Found: {latestSession.totalJobsFound}</Text>
            <Text fontSize="sm">Jobs Created: {latestSession.totalJobsCreated}</Text>
          </VStack>

          {progressInfo.length > 0 && (
            <VStack spacing={2} align="start" width="100%" maxW="400px">
              <Text fontSize="md" fontWeight="semibold">Website Progress:</Text>
              {progressInfo.map((progress) => (
                <Box key={progress.id} width="100%">
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" noOfLines={1}>
                      {"Overall progress" || progress.websiteName}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {Math.round(progress.progressPercent)}%
                    </Text>
                  </HStack>
                  <Progress 
                    value={progress.progressPercent} 
                    size="sm" 
                    colorScheme="green"
                    borderRadius="sm"
                  />
                  {progress.currentJobUrl && (
                    <Text fontSize="xs" color="gray.500" mt={1} noOfLines={1}>
                      Current: {progress.currentJobUrl}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </VStack>
      </Center>
    );
  };

  // Function to render completed session content
  const renderCompletedContent = () => {
    return (
      <Center 
        flexDir="column" 
        width="100%" 
        minH="500px" 
        border="1px" 
        borderColor="neutral.600" 
        borderRadius="12px" 
        gap="20px"
      >
        <Stack>
          <Center flexDir="column" gap="12px">
            <FiCheckCircle size="24px" color="green" />
            <Text fontSize="2xl" fontWeight="bold">
              Site Connected
            </Text>
            <Text maxW="360px" textAlign="center">
              {currentUser?.agency?.jobWebsite}
            </Text>
            {latestSession && (
              <VStack spacing={1} mt={4}>
                <Text fontSize="sm" color="gray.600">Last sync completed:</Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {new Date(latestSession.completedAt).toLocaleString()}
                </Text>
                <HStack spacing={4} mt={2}>
                  <Text fontSize="xs">Jobs Found: {latestSession.totalJobsFound}</Text>
                  <Text fontSize="xs">Created: {latestSession.totalJobsCreated}</Text>
                  <Text fontSize="xs">Success Rate: {(latestSession.successRate).toFixed(2)}%</Text>
                </HStack>
              </VStack>
            )}
          </Center>
        </Stack>
        <HStack>
          <Button
            backgroundColor="white"
            color="black"
            leftIcon={<FiRefreshCw />}
            onClick={handleTriggerScraping}
            isLoading={isTriggeringSync}
            loadingText="Starting..."
            disabled={isTriggeringSync}
          >
            ReSync
          </Button>
        </HStack>
      </Center>
    );
  };

  return (
    <Flex h="100%" w="100%" direction="column">
      <JobsPageHeader />
      {currentUser?.agency?.jobWebsite ? (
        <>
          {/* Show progress if session is active, otherwise show completed content */}
          {latestSession && latestSession.status !== 'COMPLETED' 
            ? renderProgressContent() 
            : renderCompletedContent()
          }
        </>
      ) : (
        <Center 
          flexDir="column" 
          width="100%" 
          minH="500px" 
          border="1px" 
          borderColor="neutral.600" 
          borderRadius="12px" 
          gap="20px"
        >
          <Stack>
            <Center flexDir="column" gap="12px">
              <FiGlobe size="24px"/>
              <Text fontSize="2xl" fontWeight="bold">
                No site connected
              </Text>
              <Text maxW="360px" textAlign="center">
                Add your careers page URL to start syncing jobs automatically each day.
              </Text>
            </Center>
          </Stack>
          <HStack>
            <InputGroup>
              <InputLeftElement pointerEvents='none'>
                <FiGlobe color='gray.300' />
              </InputLeftElement>
              <Input 
                type='url' 
                placeholder="https://example.com/jobs" 
                minW="400px"
                value={jobWebsiteUrl}
                onChange={(e) => {
                  setJobWebsiteUrl(e.target.value);
                  if (!isValidUrl) setIsValidUrl(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleJobWebsiteSubmit();
                  }
                }}
                borderColor={!isValidUrl ? "red.300" : "gray.300"}
              />
            </InputGroup>
            <Button 
              colorScheme="white" 
              variant="outline" 
              leftIcon={<FiRefreshCw />}
              onClick={handleJobWebsiteSubmit}
              isLoading={isUpdating}
              loadingText="Connecting..."
            >
              Connect
            </Button>
          </HStack>
          {!isValidUrl && (
            <Text color="red.300" fontSize="sm" mt={2}>
              Please enter a valid URL (must start with http:// or https://)
            </Text>
          )}
        </Center>
      )}
    </Flex>
  );
}

export default WithAppLayout(SyncJobsPage);
